import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { Schedule } from "@models/schedule";
import { ExperienceId } from "@models/experienceId";
import { Result } from "@models/result";
import { Coordinates } from "@models/locationCoordinates";
import { LocationInfo } from "@models/locationInfo";
import { Counter } from "@models/counter";
import { AppConfigService } from "@services/config.service";
import { ExperienceVersionDetails } from "@models/experience-version-details.model.";

@Injectable({
  providedIn: "root",
})
export class ExperiencesService {
  private url: string;
  constructor(private http: HttpClient, public config: AppConfigService) {
    this.url = config.get<string>((x) => x.keys_apis.api);
  }

  getExperienceVersionDetails(
    experienceId: string
  ): Observable<Result<ExperienceVersionDetails>> {
    return this.http.get<Result<ExperienceVersionDetails>>(
      this.url + "/v1/experiences/" + experienceId + "/notpublished"
    );
  }

  getExperienceCount(): Observable<Counter> {
    return this.http.get<Counter>(this.url + "/v1/experiences/count");
  }

  getExperienceByStatus(draft: string): Observable<any> {
    return this.http.get<any>(this.url + "/v1/experiences?status=" + draft);
  }

  getMoreExperiences(
    status: string,
    skip: number,
    take: number
  ): Observable<any> {
    return this.http.get<any>(
      this.url +
        "/v1/experiences?status=" +
        status +
        "&take=" +
        take +
        "&skip=" +
        skip
    );
  }

  createExperience(): Observable<any> {
    return this.http.post<Result<ExperienceId>>(
      this.url + "/v1/experiences",
      {}
    );
  }

  scheduleExperience(
    experienceId: string,
    weekdays: Array<number>,
    startDate: string,
    endDate: string
  ): Observable<Schedule> {
    return this.http.post<Schedule>(
      this.url + "/v1/experiences/" + experienceId + "/schedule-period",
      { weekdays: weekdays, startDate: startDate, endDate: endDate }
    );
  }

  removeSchedule(
    experienceId: string,
    schedule: Schedule
  ): Observable<Schedule> {
    return this.http.put<Schedule>(
      this.url + "/v1/experiences/" + experienceId + "/schedule-period",
      schedule
    );
  }

  setExperienceType(experienceId: string, type: string): Observable<any> {
    return this.http.put<any>(
      this.url + "/v1/experiences/" + experienceId + "/type",
      { type: type }
    );
  }

  setExperienceCoordinates(
    experienceId: string,
    latitude: number,
    longtitude: number
  ): Observable<Coordinates> {
    return this.http.put<Coordinates>(
      this.url + "/v1/experiences/" + experienceId + "/location/coordinates",
      { latitude: latitude, longitude: longtitude }
    );
  }

  addLocationInfo(
    experienceId: string,
    addresstext: string,
    description: string,
    city: string,
    country: string,
    languageTag: string,
    addressName?: string
  ): Observable<LocationInfo> {
    return this.http.post<LocationInfo>(
      this.url + "/v1/experiences/" + experienceId + "/location/info",
      {
        addressText: addresstext,
        description: description,
        languageTag: languageTag,
        city: city,
        country: country,
        addressName: addressName,
      }
    );
  }

  setCoverImage(experienceId: string, imageId: string): Observable<any> {
    return this.http.put<any>(
      this.url + "/v1/experiences/" + experienceId + "/cover-image",
      { imageId: imageId }
    );
  }

  addImageToGallery(experienceId: string, imageId: string): Observable<any> {
    return this.http.post<any>(
      this.url + "/v1/experiences/" + experienceId + "/gallery",
      { imageId: imageId }
    );
  }

  removeImageFromGallery(
    experienceId: string,
    imageId: string
  ): Observable<any> {
    return this.http.put<any>(
      this.url + "/v1/experiences/" + experienceId + "/gallery/" + imageId,
      {}
    );
  }

  addExperienceInformation(
    experienceId: string,
    title: string,
    description: string,
    languageTag: string
  ): Observable<any> {
    return this.http.put<any>(
      this.url + "/v1/experiences/" + experienceId + "/info/",
      { title: title, description: description, languageTag: languageTag }
    );
  }

  publishForApproval(experienceId: string): Observable<any> {
    return this.http.post<any>(
      this.url + "/v1/experiences/" + experienceId + "/publish",
      {}
    );
  }

  binExperience(experienceId: string): Observable<any> {
    return this.http.delete<any>(
      this.url + "/v1/experiences/" + experienceId + "/bin",
      {}
    );
  }

  binExperienceCancel(experienceId: string): Observable<any> {
    return this.http.delete<any>(
      this.url + "/v1/experiences/" + experienceId + "/cancel",
      {}
    );
  }

  deleteExperience(experienceId: string): Observable<any> {
    return this.http.delete<any>(
      this.url + "/v1/experiences/" + experienceId,
      {}
    );
  }

  editPublished(experienceId: string): Observable<any> {
    return this.http.post<any>(
      this.url + "/v1/experiences/" + experienceId + "/published",
      {}
    );
  }

  editRejected(experienceId: string): Observable<any> {
    return this.http.post<any>(
      this.url + "/v1/experiences/" + experienceId + "/rejected",
      {}
    );
  }

  restore(experienceId: string): Observable<any> {
    return this.http.post<any>(
      this.url + "/v1/experiences/" + experienceId + "/restore",
      {}
    );
  }

  getQr(experienceId: string): Observable<any> {
    return this.http.get<any>(
      this.url + "/v1/experiences/" + experienceId + "/qrCode",
      { responseType: "blob" as "json" }
    );
  }

  setFbToken(token: string): Observable<any> {
    return this.http.put<any>(this.url + "/v1/accounts/facebook/token", {
      fbToken: token,
    });
  }

  getFbExperiences(): Observable<any> {
    return this.http.get<any>(this.url + "/v1/accounts/facebook/events");
  }

  importFbExperience(id: string): Observable<any> {
    return this.http.post<any>(this.url + "/v1/accounts/facebook/events", {
      eventId: id,
    });
  }
}
